import type { Ref } from 'vue';
import { useSearch, useSearchService } from '@/shared/lib';
import type { MemberSearchField, SearchParams, Pagination } from '@/shared/lib';

type EntitySearchFunction<T> = (
  endpoint: string,
  searchParams: SearchParams
) => Promise<{ data: T[]; metadata: Pagination }>;

interface EntitySearchConfig<T> {
  defaultSearchUrl: string;
  searchFunction: EntitySearchFunction<T>;
  searchFieldRef?: Ref<MemberSearchField>;
  skipShowAllPrefetch?: boolean;
}

export function useEntitySearch<T extends { id: number }>(
  config: EntitySearchConfig<T>,
  params: SearchParams
) {
  const searchService = useSearchService<T>({
    searchUrl: params.searchUrl ?? config.defaultSearchUrl,
    context: params.context ?? 'search',
    searchFunction: config.searchFunction,
  });

  const searchFunction = async (searchParams: SearchParams) => {
    await searchService.searchItems(searchParams);
    return {
      data: searchService.items.value as T[],
      metadata: searchService.metadata.value as Pagination,
    };
  };

  return useSearch<T>({
    ...params,
    searchFunction,
    ...(config.searchFieldRef ? { searchFieldRef: config.searchFieldRef } : {}),
    ...(config.skipShowAllPrefetch ? { skipShowAllPrefetch: true } : {}),
  });
}
